import { TreeGridColumn } from "../types"
import { ApprovedHoursColumnName, ApprovedHoursVisibleColumnName } from "./types"
import type { HourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"

export function makeColumns({
  columnOrder,
  visibleColumns,
  workflowSettings,
  labels,
}: MakeColumnsProps): TreeGridColumn[] {
  const isApprovalWorkflow = workflowSettings?.isApprovalWorkflow ?? false
  const actionsColumn = {
    Name: "actions",
    Type: "Html",
    Width: 64,
    Class: "approved-hours-actions-cell",
    CanEdit: 0,
    CanExport: 0,
    CanPrint: 0,
    CanSort: 0,
    CanHide: 0,
    header: "",
  }

  const dateColumn = {
    Name: "date",
    Width: 160,
    CanEdit: 0,
    Visible: visibleColumns.includes("date"),
    header: labels.date,
  }

  const projectOrTaskColumn = {
    Name: "projectOrTask",
    Type: "Html",
    Width: 240,
    MinWidth: 220,
    RelWidth: visibleColumns.includes("description") ? 0 : 1,
    CanEdit: 0,
    CanSort: 0,
    Visible: visibleColumns.includes("projectOrTask"),
    SearchNames: "projectOrTask,projectOrTaskSearchValue",
    header: labels.projectTask,
  }

  const descriptionColumn = {
    Name: "description",
    Type: "Lines",
    RelWidth: 1,
    MinWidth: 220,
    CanEdit: 0,
    CanSort: 0,
    Visible: visibleColumns.includes("description"),
    header: labels.description,
  }

  const workerColumn = {
    Name: "worker",
    CanEdit: 0,
    Visible: visibleColumns.includes("worker"),
    header: labels.worker,
  }

  const startedColumn = {
    Name: "started",
    CanEdit: 0,
    Visible: visibleColumns.includes("started"),
    header: labels.started,
  }

  const finishedColumn = {
    Name: "finished",
    CanEdit: 0,
    Visible: visibleColumns.includes("finished"),
    header: labels.finished,
  }

  const hoursColumn = {
    Name: "hours",
    CanEdit: 0,
    Visible: visibleColumns.includes("hours"),
    header: labels.hours,
    Formula: "Grids.FormatTotalApprovedHours(sum('durationInDecimalHours', '4'))",
    Class: "reported-hours-total-value",
  }

  const readyForCheckColumn = {
    Name: "readyForCheck",
    Type: "Bool",
    CanEdit: 1,
    Visible: visibleColumns.includes("readyForCheck"),
    header: isApprovalWorkflow ? labels.readyForApproval : labels.readyForCheck,
  }

  const readyForApprovalColumn = {
    Name: "readyForApproval",
    Type: "Bool",
    CanEdit: 1,
    Visible: !isApprovalWorkflow && visibleColumns.includes("readyForApproval"),
    CanHide: isApprovalWorkflow ? 0 : 1,
    header: labels.readyForApproval,
  }

  const approvedColumn = {
    Name: "approved",
    Type: "Bool",
    CanEdit: 1,
    Visible: visibleColumns.includes("approved"),
    header: labels.approved,
  }

  const durationInDecimalHoursColumn = {
    Name: "durationInDecimalHours",
    Visible: 0,
    CanExport: 0,
    CanPrint: 0,
    CanHide: 0,
  }

  const projectOrTaskExportValueColumn = makeHiddenMetadataColumn("projectOrTaskExportValue")
  const statusColumn = makeHiddenMetadataColumn("status")
  const reporterUserIdColumn = makeHiddenMetadataColumn("reporterUserId")
  const reporterUserFullNameColumn = makeHiddenMetadataColumn("reporterUserFullName")

  const panelColumn = {
    Name: "Panel",
    Visible: 0,
    CanEdit: 0,
    CanExport: 0,
    CanFilter: 0,
    CanFocus: 0,
    CanGroup: 0,
    CanHide: 0,
    CanMove: 0,
    CanPrint: 0,
    CanResize: 0,
    CanSort: 0,
  }

  const columnsByName: Record<ApprovedHoursColumnName, TreeGridColumn> = {
    actions: actionsColumn,
    date: dateColumn,
    projectOrTask: projectOrTaskColumn,
    description: descriptionColumn,
    worker: workerColumn,
    started: startedColumn,
    finished: finishedColumn,
    hours: hoursColumn,
    readyForCheck: readyForCheckColumn,
    readyForApproval: readyForApprovalColumn,
    approved: approvedColumn,
    projectOrTaskExportValue: projectOrTaskExportValueColumn,
    durationInDecimalHours: durationInDecimalHoursColumn,
    status: statusColumn,
    reporterUserId: reporterUserIdColumn,
    reporterUserFullName: reporterUserFullNameColumn,
    Panel: panelColumn,
  }

  return [
    actionsColumn,
    ...columnOrder.map((columnName) => columnsByName[columnName]),
    projectOrTaskExportValueColumn,
    durationInDecimalHoursColumn,
    statusColumn,
    reporterUserIdColumn,
    reporterUserFullNameColumn,
    panelColumn,
  ]
}

const makeHiddenMetadataColumn = (name: ApprovedHoursColumnName): TreeGridColumn => ({
  Name: name,
  Visible: 0,
  CanEdit: 0,
  CanExport: 0,
  CanPrint: 0,
  CanHide: 0,
})

type MakeColumnsProps = {
  visibleColumns: ApprovedHoursVisibleColumnName[]
  columnOrder: ApprovedHoursVisibleColumnName[]
  workflowSettings?: HourReportingWorkflowSettings
  labels: ApprovedHoursColumnLabels
}

export type ApprovedHoursColumnLabels = {
  date: string
  projectTask: string
  description: string
  worker: string
  started: string
  finished: string
  hours: string
  readyForCheck: string
  readyForApproval: string
  approved: string
}
